import React, { useState, useMemo, useDeferredValue } from 'react';
import { AlertCircle } from 'lucide-react';
import { scanNotesForSoftWarnings } from './scanner';

/**
 * SoftWarningChip — tiny inline advisory below the textarea (Feb 26, 2026).
 *
 * Lifted into the conflict-scanner feature folder as part of Move 5 so all
 * pre-generate input validation UI lives in one place. Renders a single
 * muted line like "⚠ 2 soft flags · hover to view" with a lightweight hover
 * popover listing each flag. Non-blocking; Generate proceeds even if the
 * provider ignores it. Auto-hides when the scanner returns an empty array.
 *
 * Feb 27, 2026 — switched from useMemo(scanner, [notes]) to
 * useDeferredValue. React 19's built-in priority system runs the scan with
 * lower priority than the typing/render path, so heavy regex work doesn't
 * block keystrokes. No manual debounce timing means tests don't have to
 * guess wait windows.
 *
 * Props:
 *   • notes (string) — current notes textarea content
 */
const SoftWarningChip = ({ notes }) => {
  // useDeferredValue lets React run the scan with low priority. During
  // rapid typing, React keeps the OLD deferred value while the textarea
  // updates urgently; once typing pauses, the deferred value catches up.
  const deferredNotes = useDeferredValue(notes);
  const warnings = useMemo(() => scanNotesForSoftWarnings(deferredNotes), [deferredNotes]);
  const [clickOpen, setClickOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const showPopover = clickOpen || hovered;
  if (!warnings || warnings.length === 0) return null;
  return (
    <div
      className="relative -mt-1 text-[11px] leading-tight"
      data-testid="soft-warnings-chip"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        type="button"
        onClick={() => setClickOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-amber-300/90 hover:text-amber-200 bg-amber-500/5 hover:bg-amber-500/10 border border-amber-500/20 transition-colors"
        data-testid="soft-warnings-trigger"
        title="Click for details"
      >
        <AlertCircle className="w-3 h-3" />
        <span>
          {warnings.length} soft flag{warnings.length === 1 ? '' : 's'}
          <span className="text-amber-300/50 ml-1.5">·</span>
          <span className="text-amber-300/70 ml-1.5 font-normal">hover for details</span>
        </span>
      </button>
      {showPopover && (
        <div
          className="absolute z-30 left-0 top-full mt-1 w-[min(420px,calc(100vw-2rem))] p-2 rounded-lg border border-amber-500/30 bg-popover shadow-xl"
          data-testid="soft-warnings-popover"
        >
          <ul className="space-y-1.5">
            {warnings.map((w) => (
              <li
                key={w.id}
                className="text-[11px] text-foreground/90 leading-snug"
                data-testid={`soft-warning-${w.id}`}
              >
                <span className="font-semibold text-amber-300">{w.label}</span>
                <span className="text-muted-foreground"> — {w.detail}</span>
              </li>
            ))}
          </ul>
          <div className="text-[10px] text-muted-foreground/70 mt-1.5 pt-1.5 border-t border-border italic">
            Advisory only — Generate is not blocked.
          </div>
        </div>
      )}
    </div>
  );
};

export default SoftWarningChip;
